import { useEffect, useState } from "react";
import { CashDrawerSettings } from "../interfaces/settings.interface";
import { Button, Card, Form, Input, Select, Space, Switch, Typography, message } from "antd";
import axiosClient from "../api/axiosClient";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { createCashDrawerSettings, getCashDrawerSettings, updateCashDrawerSettings, openCashDrawer } from "../store/settingsSlice";

const { Title, Text } = Typography;

const defaultValues: CashDrawerSettings = {
  connectionMode: "local",
  shellyHost: null,
  shellyUsername: null,
  shellyPassword: null,
  autoOpenOnCashSale: true,
};

interface Props {
  businessId: string;
}

export default function CashDrawerSettingsPage({ businessId }: Props) {
  const [form] = Form.useForm<CashDrawerSettings>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settingsExist, setSettingsExist] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { opening } = useSelector((state: RootState) => state.cashDrawer);


  useEffect(() => {
    dispatch(getCashDrawerSettings(businessId));
  }, [businessId]);


  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        connectionMode: values.connectionMode,
        shellyHost: values.shellyHost,
        shellyUsername: values.shellyUsername,
        shellyPassword: values.shellyPassword,
        autoOpenOnCashSale: values.autoOpenOnCashSale,
      };
  
      if (settingsExist) {
        await dispatch(
          updateCashDrawerSettings({
            businessId,
            data: payload,
          })
        ).unwrap();
        message.success("Cash drawer settings updated.");
      } else {
        await dispatch(
          createCashDrawerSettings({
            businessId,
            data: payload,
          })
        ).unwrap();
        message.success("Cash drawer settings created.");
        setSettingsExist(true);
      }
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(
        typeof error === "string"
          ? error
          : "Failed to save cash drawer settings."
      );
    }
  };

  const handleOpenDrawerTest = async () => {
    try {
      await dispatch(openCashDrawer(businessId)).unwrap();
      message.success("Drawer open command sent.");
    } catch (error: any) {
      message.error(
        typeof error === "string" ? error : "Failed to open drawer."
      );
    }
  };

  return (
    <Card loading={loading}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={3}>Cash Drawer Settings</Title>
          <Text type="secondary">
            Configure the cash drawer connection for this business.
          </Text>
        </div>

        <Form form={form} layout="vertical" initialValues={defaultValues}>
          <Form.Item
            label="Connection Mode"
            name="connectionMode"
            rules={[{ required: true, message: "Please select a connection mode." }]}
          >
            <Select
              options={[
                { label: "Local Network", value: "local" },
                { label: "Shelly Cloud (coming soon)", value: "cloud", disabled: true },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Shelly IP / Hostname"
            name="shellyHost"
            rules={[
              { required: true, message: "Please enter the Shelly IP or hostname." },
            ]}
          >
            <Input placeholder="192.168.1.92" />
          </Form.Item>

          <Form.Item label="Shelly Username" name="shellyUsername">
            <Input placeholder="Optional" />
          </Form.Item>

          <Form.Item label="Shelly Password" name="shellyPassword">
            <Input.Password placeholder="Optional" />
          </Form.Item>

          <Form.Item
            label="Auto-open after cash sale"
            name="autoOpenOnCashSale"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" onClick={handleSave} loading={saving}>
              Save
            </Button>
          </Form.Item>
        </Form>
        <Button onClick={handleOpenDrawerTest} loading={opening}>
            Test Connection
        </Button>
      </Space>
    </Card>
  );
}